import { Fragment } from "react";
import { motion } from "framer-motion";
import { IMG } from "@/data";
import { Alpana } from "@/components/Logo";

function WalkStop({ img, eyebrow, title, desc, onClick, testid, flip }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 44 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
      onClick={onClick}
      className="group grid cursor-pointer overflow-hidden rounded-2xl border border-gold/20 bg-earthen/70 md:grid-cols-2"
      data-testid={testid}
    >
      <div className={`relative h-[340px] overflow-hidden md:h-[440px] ${flip ? "md:order-2" : ""}`}>
        <img
          src={img}
          alt={title}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-dusk/40 via-transparent to-transparent" />
        <span className="absolute left-5 top-5 rounded-full border border-gold/35 bg-dusk/70 px-4 py-1.5 text-[11px] tracking-wide text-gold backdrop-blur-md">
          এখন খোলা
        </span>
      </div>
      <div className="flex flex-col justify-center p-7 sm:p-10">
        <p className="font-latin text-xs italic tracking-[0.25em] text-gold/85">{eyebrow}</p>
        <h3 className="mt-3 font-display text-4xl text-alpana sm:text-5xl">{title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-sand/75">{desc}</p>
        <button
          data-testid={`${testid}-enter`}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="mt-6 inline-flex w-max items-center gap-2 rounded-full border border-alpana/30 bg-dusk/40 px-5 py-2 text-sm text-alpana backdrop-blur-md transition-all duration-500 hover:gap-4 hover:border-gold hover:bg-gold hover:text-dusk"
        >
          ঘরে ঢুন
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </motion.div>
  );
}

export default function Haat({ navTo, site }) {
  const stops = site?.shops?.length
    ? site.shops.map((s) => ({
        key: s.key,
        img: s.image || IMG.shop,
        eyebrow: s.latin,
        title: s.name,
        desc: s.blurb,
      }))
    : [
        {
          key: "potchitra",
          img: IMG.shop,
          eyebrow: "THE SCROLL-PAINTERS' HOUSE",
          title: "পটচিত্র ঘর",
          desc: "মেদিনীপুরের পটুয়াদের নিজের ঘর — হাতে আঁকা চিত্রপট, টেরাকোটা আর তাঁতের শাড়ি এখানে সাজানো।",
        },
        {
          key: "mrityo",
          img: IMG.pottery_shop,
          eyebrow: "THE POTTERS' HOUSE",
          title: "মৃৎশিল্পের ঘর",
          desc: "কুমারপাড়ার কুমারদের উঠান — চাক ঘোরে, মাটি গড়ায়, হাঁড়ি-হান্দি আগুনে পোড়ে।",
        },
      ];
  const stopId = (key) => (key === "potchitra" ? "shop-card-patachitra" : key === "mrityo" ? "shop-card-mrityo" : `shop-card-${key}`);
  return (
    <div>
      <section className="relative flex h-[68vh] min-h-[460px] items-end overflow-hidden">
        <img src={IMG.lane} alt="রঙের হাট" data-testid="haat-hero-image" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-dusk via-dusk/25 to-dusk/55" />
        <div className="night-glow" />
        <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-14 sm:px-10">
          <motion.button
            data-testid="back-to-village"
            onClick={() => navTo("gate", "গ্রাম-প্রবেশ")}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="mb-7 flex items-center gap-2 rounded-full border border-sand/25 bg-dusk/40 px-4 py-1.5 text-xs text-sand/85 backdrop-blur-md transition-colors hover:border-gold/60 hover:text-gold"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            গ্রামের গেটে ফিরুন
          </motion.button>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.7 }}
            className="font-latin text-xs italic tracking-[0.3em] text-gold/85 drop-shadow-[0_1px_6px_rgba(0,0,0,0.9)]"
          >
            THE CRAFT MARKET
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-3 font-display text-6xl text-alpana sm:text-7xl"
          >
            রঙের হাট
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.65, duration: 0.7 }}
            className="mt-3 text-base text-sand/80"
          >
            হস্তশিল্প • পোশাক • গৃহসজ্জা • উপহার
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-[1300px] px-6 py-16 sm:px-10 sm:py-20">
        <div className="mb-12 flex flex-col items-center text-center">
          <Alpana className="w-32" />
          <h2 className="mt-6 font-display text-3xl text-sand sm:text-4xl">গ্রামের পথে হাঁটুন</h2>
          <p className="mt-3 font-latin text-sm italic text-gold/70">
            Walk down the lane — push open the doors that call you
          </p>
        </div>

        <div className="space-y-6">
          {stops.map((s, i) => (
            <Fragment key={s.key}>
              {i === 1 && (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                  className="relative h-[300px] overflow-hidden rounded-2xl border border-gold/15 md:h-[360px]"
                  data-testid="pond-stop"
                >
                  <img src={IMG.pond} alt="গ্রামের পুকুর" loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dusk/80 via-transparent to-transparent" />
                  <div className="night-glow" />
                  <div className="absolute bottom-0 left-0 p-6 sm:p-8">
                    <p className="font-display text-xl text-sand sm:text-2xl">পথের ধারে পুকুর — একটু থেমে যান</p>
                    <p className="mt-1 font-latin text-xs italic text-sand/60">A quiet moment by the village pond</p>
                  </div>
                </motion.div>
              )}
              <WalkStop
                testid={stopId(s.key)}
                img={s.img}
                eyebrow={s.eyebrow}
                title={s.title}
                desc={s.desc}
                flip={i % 2 === 1}
                onClick={() => navTo("shop", s.title, s.key)}
              />
            </Fragment>
          ))}
        </div>
      </section>
    </div>
  );
}
